import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useStableCallback } from "../../hooks/useStableCallback";
import { getOffset } from "../../utils/dom";

import "./tooltipOld.css";

function useRaf(onRaf: () => void, enable = true) {
	const onStableRaf = useStableCallback(onRaf);
	const rafIdRef = useRef(0);
	useEffect(() => {
		if (!enable) return () => undefined;
		function rafStep() {
			onStableRaf();
			rafIdRef.current = window.requestAnimationFrame(rafStep);
		}
		rafStep();
		return () => window.cancelAnimationFrame(rafIdRef.current);
	}, [onStableRaf, enable]);
}

function updateTooltipPosition(
	tooltipEl: HTMLDivElement | null | undefined,
	tooltipArrowEl: HTMLDivElement | null | undefined,
	containerRect: eXpress.math.Rect | undefined,
	toolTipSize: eXpress.math.IVector | undefined,
	/** The rect in which we want to constrain the tooltip (default screen size) */
	boundsRect: eXpress.math.Rect | undefined,
	direction: "up" | "down" | "left" | "right",
	margin: number | undefined = 5,
) {
	if (!tooltipEl || !tooltipArrowEl) return;

	// Hide when we cannot show the correct position
	if (!containerRect || !toolTipSize || !boundsRect) {
		tooltipEl.style.opacity = "0";
		tooltipArrowEl.style.opacity = "0";
		return;
	}
	tooltipEl.style.opacity = "1";
	tooltipArrowEl.style.opacity = "1";

	const arrowSize = 5;
	let left = 0;
	let top = 0;
	let arrowLeft = 0;
	let arrowTop = 0;
	let maxWidth = boundsRect.width;

	switch (direction) {
		case "up": {
			left = containerRect.center().left - toolTipSize.width / 2;
			top = containerRect.top - toolTipSize.height - arrowSize - margin;

			// Constraint horizontal size in bounds
			if (left + toolTipSize.width > boundsRect.left + boundsRect.width) {
				left = boundsRect.left + boundsRect.width - toolTipSize.width;
			}
			if (left < boundsRect.left) left = boundsRect.left;

			arrowLeft = containerRect.center().left - arrowSize;
			arrowTop = containerRect.top - arrowSize - margin - 0.5; // - 0.5 to make sure the arrow sticks to the tooltip
			break;
		}
		case "down": {
			throw new Error('Not implemented yet: "down" case');
		}
		case "left": {
			throw new Error('Not implemented yet: "left" case');
		}
		case "right": {
			left = containerRect.left + containerRect.width + arrowSize + margin;
			top = containerRect.center().top - toolTipSize.height / 2;
			maxWidth = boundsRect.left + boundsRect.width - left; // prevent the tooltip goes beyond the left arrow

			arrowLeft = containerRect.left + containerRect.width + margin + 0.5; // + 0.5 to make sure the arrow sticks to the tooltip
			arrowTop = containerRect.center().top - arrowSize;
			break;
		}
		default: {
			eXpress.utils.exhaustiveFail(direction);
		}
	}

	tooltipEl.style.maxWidth = `${maxWidth}px`;
	tooltipEl.style.transform = `translate(${left}px, ${top}px)`;
	tooltipArrowEl.style.transform = `translate(${arrowLeft}px, ${arrowTop}px)`;
}

const Portal = ({ children }: React.PropsWithChildren<Record<never, unknown>>) => {
	const portalEl = document.getElementById("tooltip-layer");
	return portalEl && createPortal(children, portalEl);
};

export const TooltipOld = ({
	children,
	message,
	show,
	direction,
	onClick,
	margin,
	width,
}: React.PropsWithChildren<{
	message: string;
	show: boolean;
	direction: "up" | "down" | "left" | "right";
	/** Distance in pixels to stay from the element (Default: 5) */
	margin?: number;
	width?: number | string;
	onClick?: () => void;
}>) => {
	const containerRef = useRef<HTMLSpanElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const tooltipArrowRef = useRef<HTMLDivElement>(null);

	const [showTooltip, setShowTooltip] = useState(show);

	useEffect(() => {
		setShowTooltip(show);
	}, [show]);

	// Before there was also an infinite loop from useResizeObserver, now raf used
	// TODO: Find a better way to detected changes to these elements
	useRaf(() => {
		// Get position/size of the element to follow
		const containerBoundingRect = containerRef.current?.getBoundingClientRect();
		const containerTopLeft = containerRef.current ? getOffset(containerRef.current) : undefined;
		const containerRect =
			containerTopLeft && containerBoundingRect
				? eXpress.math.rect(
						containerTopLeft.left,
						containerTopLeft.top,
						containerBoundingRect.width,
						containerBoundingRect.height,
				  )
				: undefined;

		// Get the size of the tooltip
		const tooltipRect = tooltipRef.current?.getBoundingClientRect();
		const toolTipSize = {
			width: tooltipRect?.width ?? 0,
			height: tooltipRect?.height ?? 0,
		};

		// Stay 10 pixels from the side
		const portalEl = document.getElementById("tooltip-layer"); // Later find a parent tooltip portal? containerRef.current?.closest(".tooltip-layer");
		const portalRect = portalEl
			? eXpress.math.rect(portalEl?.getBoundingClientRect()).shrink(10)
			: undefined;

		updateTooltipPosition(
			tooltipRef.current,
			tooltipArrowRef.current,
			containerRect,
			toolTipSize,
			portalRect,
			direction,
			margin,
		);
	}, showTooltip);

	const closeTooltip = () => {
		if (onClick) onClick();
		setShowTooltip(false);
	};

	return (
		<span ref={containerRef}>
			{children}
			{showTooltip && (
				<Portal>
					<div
						className={`tooltip-container__tooltip-arrow tooltip-container__tooltip-arrow--${direction}`}
						ref={tooltipArrowRef}
					/>
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
					<div
						role="tooltip"
						className="tooltip-container"
						style={{ opacity: 0, width }}
						onClick={closeTooltip}
						ref={tooltipRef}
					>
						<div className="tooltip-content">{message}</div>
					</div>
				</Portal>
			)}
		</span>
	);
};

/** A div where tooltips from children are shown */
export function TooltipLayer() {
	return <div id="tooltip-layer" />;
}
